import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useGetProductReviewRatings from '../../../hooks/queries/useGetProductReviewRatings';
import useGetPiiEncryptionKey from '../../../hooks/queries/useGetPiiEncryptionKey';
import encryptReviewRequestEmail from '../../../utils/productReviews/encryptReviewRequestEmail';
import {
  calculateSubtotal,
  calculateTotalDiscounts,
  calculateTotalGiftCards,
  calculateTotalPromo,
  calculatetShippingTotal,
  calculateWarrantyPrice,
  calculateTotalPromoBeddingRemaining,
  calculateTotalPromoBedding,
  calculateTaxesExcludingDeliveryFee,
  getRetailDeliveryFee,
  getTotalItems,
  priceToString,
} from '../../../utils';
import styles from './ThankyouSummary.module.scss';
import messages from './messages';
import utilMessages from '../../../utils/messages';
import WhatIsThisFee from '../../../components/WhatIsThisFee';
import {
  FEEL_VARIANT_NAME_COOKIE,
  FEEL_ATTRIBUTE_NAME,
} from '../../../utils/common/constants';
import { getCookie } from '../../../utils/common/common';
import AccountsService from '../../../services/customer-accounts';
import { findVariantAttributeByName } from '../../../utils/variants';
import AMEXIcon from '../../../assets/images/svg/amex-icon.svg';
import DiscoverIcon from '../../../assets/images/svg/discover-icon.svg';
import MastercardIcon from '../../../assets/images/svg/mastercard-icon.svg';
import VisaIcon from '../../../assets/images/svg/visa-icon.svg';
import { ABANDONED_CHECKOUT_EXPIRY_COOKIE, ABANDONED_CHECKOUT_EXPIRY_VALUE } from '../../AbandonedCartToasts/constants';

const creditCardIcon = (creditCardMethod, formatMessage) => {
  const brand = creditCardMethod.cardBrand.toUpperCase();
  switch (brand) {
    case 'VISA':
      return VisaIcon;
    case 'MASTERCARD':
      return MastercardIcon;
    case 'MC':
      return MastercardIcon;
    case 'AMEX':
      return AMEXIcon;
    case 'DISCOVER':
      return DiscoverIcon;
    default:
      return formatMessage(messages.creditCardEndingIn);
  }
};

const showCreditCardInfo = (payment, formatMessage) => (
  <div>
    {creditCardIcon(payment, formatMessage) ===
    formatMessage(messages.creditCardEndingIn) ? (
      <>
        {formatMessage(messages.creditCardEndingIn)}
        <span
          className={styles['thank-you-summary-credit-card-ending-in-space']}
        >
          ****
        </span>
      </>
    ) : (
      <>
        <img
          id="cc-brand"
          className={styles['thank-you-summary-credit-card-brand-logo']}
          src={creditCardIcon(payment)}
          width="40"
          height="40"
          alt={formatMessage(messages.cardLogoAltMessage)}
        />
        <span>****</span>
      </>
    )}
    {payment.cardlast4Numbers}
  </div>
);

const showStripePaymentMethod = (payment, formatMessage) => {
  if (payment.type === 'card') {
    return (
      <div>
        {creditCardIcon(payment, formatMessage) ===
        formatMessage(messages.creditCardEndingIn) ? (
          <>
            {formatMessage(messages.creditCardEndingIn)}
            <span
              className={styles['thank-you-summary-credit-card-ending-in-space']}
            >
              ****
            </span>
          </>
        ) : (
          <>
            <img
              id="cc-brand"
              className={styles['thank-you-summary-credit-card-brand-logo']}
              src={creditCardIcon(payment)}
              width="40"
              height="40"
              alt={formatMessage(messages.cardLogoAltMessage)}
            />
            <span>****</span>
          </>
        )}
        {payment.cardlast4Numbers}
      </div>
    );
  }
  return (
    <div>
      {payment.type.toUpperCase().replace('_', ' ')}
    </div>
  )
};

const displayPaymentMethods = (paymentInfo, formatMessage) => {
  const payments = paymentInfo?.payments;
  let paymentsToDisplay = [];
  if (payments) {
    paymentsToDisplay = payments.map((payment) => {
      const paymentMethod = payment.paymentMethodInfo?.method;
      if (paymentMethod) {
        switch (paymentMethod) {
          case 'CREDIT_CARD':
            return showCreditCardInfo(payment, formatMessage);
          case 'CARD':
            return showCreditCardInfo(payment, formatMessage);
          case 'AFFIRM':
            return formatMessage(messages.affirmTitle);
          case 'CHARGE_AFTER':
            return formatMessage(messages.chargeAfterTitle);
          case 'STRIPE':
            return showStripePaymentMethod(payment, formatMessage);
          case 'SHOP_PAY':
            return formatMessage(messages.shopPayTitle);
          default:
            return paymentMethod.toUpperCase().replace('_', ' ');
        }
      } else {
        return '';
      }
    });
  }

  return paymentsToDisplay;
};

const isCreditCard = (paymentInfo) =>
  paymentInfo?.payments?.some(
    (payment) => payment.paymentMethodInfo?.method === 'CREDIT_CARD' || payment.paymentMethodInfo?.method === 'CARD',
  );

const displayCreditCardExpiry = (paymentInfo, formatMessage) => {
  const payments = paymentInfo?.payments;
  let paymentsToDisplay = [];
  if (payments) {
    paymentsToDisplay = payments.map((payment) => {
      const paymentMethod = payment.paymentMethodInfo?.method;
      if (paymentMethod && payment.expMonth && payment.expYear) {
        switch (paymentMethod) {
          case 'CREDIT_CARD':
            return `${formatMessage(messages.creditCardExpiry, {
              expMonth: payment.expMonth,
              expYear: payment.expYear,
            })}`;
          case 'CARD':
            return `${formatMessage(messages.creditCardExpiry, {
              expMonth: payment.expMonth,
              expYear: payment.expYear,
            })}`;
          default:
            return '';
        }
      } else {
        return '';
      }
    });
  }

  return paymentsToDisplay;
};

const isQualifiedForFreeWhiteGloveShipping = (customLineItems) => {
  const filteredCustomLineItems = customLineItems?.filter((lineItem) =>
    [
      'whiteglove-eligible_inHomesetup_removal',
      'whiteglove-eligible_inHomesetup_leave',
    ].includes(lineItem.slug),
  );
  return filteredCustomLineItems?.length > 0;
};

const filterShippingMethodsAndGetNames = (
  customLineItems,
  locale,
  lineItems,
) => {
  const shippingMethods = customLineItems?.filter(
    (customLineItem) => customLineItem.custom?.fields?.netsuite_shipping_name,
  );

  const hideFeelCookie = getCookie(FEEL_VARIANT_NAME_COOKIE);
  const hideFeelVariantName =
    hideFeelCookie?.value === 'hide_feel_variant_test';

  const getVariantTitle = (lineItem) =>
    !!findVariantAttributeByName(lineItem.variant, FEEL_ATTRIBUTE_NAME) &&
    hideFeelVariantName
      ? lineItem.variant.attributes.find((attr) => attr.name === 'size').value
          .label
      : lineItem.variant.attributes.find((attr) => attr.name === 'title').value;

  const itemsShippingMap = {};
  shippingMethods?.forEach((shipMethod) => {
    itemsShippingMap[shipMethod?.name?.[locale]] =
      shipMethod?.custom?.fields?.lineItemsIds;
  });

  Object.keys(itemsShippingMap).forEach((shippingMethod) => {
    itemsShippingMap[shippingMethod] = lineItems
      .filter((lineItem) =>
        itemsShippingMap[shippingMethod].includes(lineItem.id),
      )
      ?.map(
        (lineItem) => `${lineItem.name[locale]} | ${getVariantTitle(lineItem)}`,
      );
  });

  return itemsShippingMap;
};

const moneyToDecimal = (price) => {
  if (!price || typeof price.centAmount !== 'number') {
    return '0.00';
  }
  const fractionDigits = price.fractionDigits ?? 2;
  return (price.centAmount / 10 ** fractionDigits).toFixed(2);
};

const getBazaarvoiceDiscountCentAmount = (totalDiscount, totalPromo) => {
  const discountCentAmount = totalDiscount?.centAmount > 0 ? totalDiscount.centAmount : 0;
  const promoCentAmount = totalPromo?.centAmount > 0 ? totalPromo.centAmount : 0;

  return discountCentAmount + promoCentAmount;
};

const getBazaarvoiceDiscount = (
  totalDiscount,
  totalPromo,
  currency,
  fractionDigits,
) =>
  moneyToDecimal({
    centAmount: getBazaarvoiceDiscountCentAmount(totalDiscount, totalPromo),
    currencyCode: totalDiscount?.currencyCode || totalPromo?.currencyCode || currency,
    fractionDigits:
      totalDiscount?.fractionDigits ??
      totalPromo?.fractionDigits ??
      fractionDigits,
  });

const getBazaarvoiceItemSubtotalCentAmount = (lineItems = []) =>
  lineItems.reduce((sum, lineItem) => {
    const quantity = lineItem?.quantity || 1;
    const unitPrice = lineItem?.price?.value;

    if (!unitPrice || typeof unitPrice.centAmount !== 'number') {
      return sum;
    }

    return sum + unitPrice.centAmount * quantity;
  }, 0);

const getBazaarvoiceTotal = (
  lineItems,
  totalDiscount,
  totalPromo,
  currency,
  fractionDigits,
) =>
  moneyToDecimal({
    centAmount: Math.max(
      getBazaarvoiceItemSubtotalCentAmount(lineItems) -
        getBazaarvoiceDiscountCentAmount(totalDiscount, totalPromo),
      0,
    ),
    currencyCode: currency,
    fractionDigits,
  });

const getLineItemUnitPrice = (lineItem) => moneyToDecimal(lineItem?.price?.value);

const getLineItemProductId = (lineItem, productReviewRatings = {}) =>
  productReviewRatings[lineItem?.productKey] || '';

const getLineItemCategory = (lineItem) => {
  const categories = lineItem?.category;
  if (!Array.isArray(categories) || !categories.length) {
    return '';
  }
  return categories[1]?.name || '';
};

const buildBazaarvoiceTransaction = ({
  order,
  lineItems,
  subtotal,
  taxes,
  totalShipping,
  totalDiscount,
  totalPromo,
  productReviewRatings,
  encryptedEmail,
  encryptionKeyId,
  locale,
}) => {
  if (!order?.orderNumber || !lineItems?.length) {
    return null;
  }

  const currency = subtotal?.currencyCode || order?.totalPrice?.currencyCode || 'USD';
  const fractionDigits = subtotal?.fractionDigits ?? order?.totalPrice?.fractionDigits ?? 2;

  const transaction = {
    orderId: order.orderNumber,
    total: getBazaarvoiceTotal(
      lineItems,
      totalDiscount,
      totalPromo,
      currency,
      fractionDigits,
    ),
    tax:
      taxes !== null
        ? moneyToDecimal({
            centAmount: taxes,
            currencyCode: currency,
            fractionDigits,
          })
        : '0.00',
    shipping: moneyToDecimal(totalShipping),
    discount: getBazaarvoiceDiscount(
      totalDiscount,
      totalPromo,
      currency,
      fractionDigits,
    ),
    currency,
    locale: locale?.replace('-', '_') || 'en_US',
    items: lineItems
      .map((lineItem) => ({
        productId: getLineItemProductId(lineItem, productReviewRatings),
        name:
          lineItem?.name?.[locale] ||
          lineItem?.name?.['en-US'] ||
          lineItem?.name?.en ||
          '',
        quantity: String(lineItem?.quantity || 1),
        price: getLineItemUnitPrice(lineItem),
        category: getLineItemCategory(lineItem),
      }))
      .filter((item) => item.productId),
    incentivizedReview: false,
  };

  if (encryptedEmail && encryptionKeyId) {
    transaction.encryptedEmail = encryptedEmail;
    transaction.encryptionKeyId = encryptionKeyId;
  }

  return transaction;
};

const ThankyouSummary = () => {
  const { formatDate, formatMessage, formatNumber, locale } = useIntl();
  const order = useSelector((state) => state.order);

  const orderDate = order?.createdAt;

  const lineItems = order?.lineItems;
  const email = order?.customerEmail || order?.email || '';
  const isTransactionEventEnabled = !!window.drupalSettings?.pur_bazaarvoice;
  const giftCards = useSelector((state) => state.order.giftCards);
  const customLineItems = order?.customLineItems;

  const {
    data: productReviewRatings = {},
    isLoading: isProductReviewRatingsLoading,
    isError: isProductReviewRatingsError,
  } = useGetProductReviewRatings(lineItems || []);

  const {
    data: piiEncryptionKey,
    isLoading: isPiiEncryptionKeyLoading,
    isError: isPiiEncryptionKeyError,
  } = useGetPiiEncryptionKey(isTransactionEventEnabled);

  /**
   * Encrypt the customer email for the Bazaarvoice transaction event.
   */
  const encryptedEmail = useMemo(() => {
    if (!email || !piiEncryptionKey?.publicKey) {
      return '';
    }

    return encryptReviewRequestEmail(email, piiEncryptionKey.publicKey);
  }, [email, piiEncryptionKey?.publicKey]);

  const { customerId, shippingAddress } = useSelector((state) => ({
    customerId: state.order?.customerId,
    shippingAddress: state.order?.shippingAddress,
  }));

  const abandonedCheckoutABTest = getCookie(
    process.env.ABANDONED_CHECKOUT_AB_TEST_COOKIE_NAME,
  )?.value;

  useEffect(() => {
    if (!!abandonedCheckoutABTest && abandonedCheckoutABTest !== ABANDONED_CHECKOUT_EXPIRY_VALUE) {
      document.cookie = ABANDONED_CHECKOUT_EXPIRY_COOKIE;
    }
  }, [abandonedCheckoutABTest]);

  useEffect(() => {
    if (customerId && shippingAddress) {
      const newAddress = {
        ...shippingAddress,
        apartment: shippingAddress.additionalStreetInfo,
      };
      if (localStorage.getItem('newAddress')) {
        AccountsService.addCustomerAddress(customerId, newAddress);
      } else if (localStorage.getItem('updateAddress')) {
        const shippingAddressId = localStorage.getItem('shippingAddressId');
        if (shippingAddressId) {
          AccountsService.updateCustomerAddress(
            customerId,
            shippingAddressId,
            newAddress,
          );
        }
      }

      localStorage.removeItem('newAddress');
      localStorage.removeItem('updateAddress');
      localStorage.removeItem('shippingAddressId');
    }
  }, [shippingAddress, customerId]);

  // Exclude the retail delivery fee tax portion; it is broken out below.
  const taxes = order?.taxedPrice
    ? calculateTaxesExcludingDeliveryFee(order.taxedPrice)
    : null;

  const totalFromStore = order?.taxedPrice
    ? order?.taxedPrice?.totalGross
    : order?.totalPrice;

  const total = useMemo(
    () =>
      totalFromStore
        ? {
            centAmount:
              totalFromStore.centAmount -
              calculateTotalGiftCards({ giftCards }),
            currencyCode: totalFromStore.currencyCode,
            fractionDigits: totalFromStore.fractionDigits,
          }
        : null,
    [giftCards, totalFromStore],
  );

  const recyclingFee = order?.customLineItems?.find(
    (customLineItem) => customLineItem?.slug === 'custom_recyclingFee',
  )?.totalPrice;

  // The retail delivery fee now arrives from Stripe as a tax portion
  // (name contains 'retail_delivery_fee') rather than a custom line item.
  const deliveryFee = getRetailDeliveryFee(order?.taxedPrice);

  const mattressRemoval = customLineItems?.find(
    (customLineItem) => customLineItem?.custom?.fields?.mattress_removal,
  );

  const subtotal = useMemo(
    () =>
      lineItems
        ? {
            centAmount: calculateSubtotal({ lineItems }),
            currencyCode: total?.currencyCode,
            fractionDigits: total?.fractionDigits,
          }
        : null,
    [lineItems, total?.currencyCode, total?.fractionDigits],
  );

  const totalDiscount = useMemo(
    () =>
      lineItems
        ? {
            centAmount: calculateTotalDiscounts({ lineItems }),
            currencyCode: total?.currencyCode,
            fractionDigits: total?.fractionDigits,
          }
        : null,
    [lineItems, total?.currencyCode, total?.fractionDigits],
  );

  const totalPromoBeddingDiscount = useMemo(
    () => ({
      centAmount: calculateTotalPromoBedding({ lineItems }),
      currencyCode: total?.currencyCode,
      fractionDigits: total?.fractionDigits,
    }),
    [lineItems, total?.currencyCode, total?.fractionDigits],
  );

  const totalPromoBeddingDiscountRemaining = useMemo(
    () => ({
      centAmount: calculateTotalPromoBeddingRemaining({ lineItems }),
      currencyCode: total?.currencyCode,
      fractionDigits: total?.fractionDigits,
    }),
    [lineItems, total?.currencyCode, total?.fractionDigits],
  );

  const totalPromo = useMemo(() => {
    if (lineItems) {
      return {
        centAmount: calculateTotalPromo({ lineItems }),
        currencyCode: total?.currencyCode,
        fractionDigits: total?.fractionDigits,
      };
    }
    return null;
  }, [lineItems, total?.currencyCode, total?.fractionDigits]);

  const totalGiftCards = useMemo(
    () => ({
      centAmount: calculateTotalGiftCards({ giftCards }),
      currencyCode: total?.currencyCode,
      fractionDigits: total?.fractionDigits,
    }),
    [giftCards, total?.currencyCode, total?.fractionDigits],
  );

  const totalShipping = useMemo(() => {
    if (customLineItems) {
      return {
        centAmount: calculatetShippingTotal({ customLineItems }),
        currencyCode: total?.currencyCode,
        fractionDigits: total?.fractionDigits,
      };
    }
    return null;
  }, [customLineItems, total?.currencyCode, total?.fractionDigits]);

  useEffect(() => {
    if (
      !isTransactionEventEnabled ||
      isProductReviewRatingsLoading ||
      isProductReviewRatingsError ||
      !email ||
      isPiiEncryptionKeyLoading ||
      isPiiEncryptionKeyError ||
      !encryptedEmail ||
      !piiEncryptionKey?.encryptionKeyId
    ) {
      return undefined;
    }

    const transaction = buildBazaarvoiceTransaction({
      order,
      lineItems,
      subtotal,
      taxes,
      totalShipping,
      totalDiscount,
      totalPromo,
      productReviewRatings,
      encryptedEmail,
      encryptionKeyId: piiEncryptionKey.encryptionKeyId,
      locale,
    });

    if (!transaction || !transaction.items.length) {
      return undefined;
    }

    const sentKey = `bv-transaction-${transaction.orderId}`;

    if (window.sessionStorage.getItem(sentKey)) {
      return undefined;
    }

    const fireBazaarvoiceTransaction = () => {
      if (
        !window.BV ||
        !window.BV.pixel ||
        typeof window.BV.pixel.trackTransaction !== 'function'
      ) {
        return false;
      }

      // Fire Bazaarvoice Transaction Event
      window.BV.pixel.trackTransaction(transaction);
      window.sessionStorage.setItem(sentKey, '1');

      return true;
    };

    if (fireBazaarvoiceTransaction()) {
      return undefined;
    }

    // custom event dispatched from product-reviews.js
    window.addEventListener('purBazaarvoiceLoaded', fireBazaarvoiceTransaction);

    return () => {
      window.removeEventListener(
        'purBazaarvoiceLoaded',
        fireBazaarvoiceTransaction,
      );
    };
  }, [
    order,
    lineItems,
    total,
    subtotal,
    taxes,
    totalShipping,
    totalDiscount,
    totalPromo,
    productReviewRatings,
    isProductReviewRatingsLoading,
    isProductReviewRatingsError,
    email,
    isTransactionEventEnabled,
    piiEncryptionKey,
    isPiiEncryptionKeyLoading,
    isPiiEncryptionKeyError,
    encryptedEmail,
    locale,
  ]);

  const warrantyTotal = useMemo(() => {
    if (customLineItems) {
      const totalWarrantyPrice = calculateWarrantyPrice({ customLineItems });
      return totalWarrantyPrice > 0
        ? {
            centAmount: totalWarrantyPrice,
            currencyCode: total?.currencyCode,
            fractionDigits: total?.fractionDigits,
          }
        : null;
    }
    return null;
  }, [customLineItems, total?.currencyCode, total?.fractionDigits]);

  const shipMethodItemMap = filterShippingMethodsAndGetNames(
    customLineItems,
    locale,
    lineItems,
  );

  localStorage.removeItem('anonymousId');

  return (
    <div className={styles.content}>
      <div className={styles.summary}>
        <div className={styles['summary-title']}>
          <div className={styles['summary-title-legend']}>
            {formatMessage(messages.title, {
              orderNumber: order?.orderNumber,
            })}
          </div>
          <div className={styles['summary-title-print']}>
            <button
              className={styles['summary-print-button']}
              type="button"
              onClick={() => {
                window.print();
              }}
            >
              {formatMessage(messages.titlePrint)}
            </button>
          </div>
        </div>
        <div className={styles['summary-order-date']}>
          <div className={styles['summary-order-date-legend']}>
            {formatMessage(messages.orderDate)}
          </div>
          <div className={styles['summary-order-date-info']}>
            {orderDate &&
              formatDate(orderDate, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
          </div>
        </div>
        <div className={styles['summary-order-items']}>
          <div className={styles['summary-order-items-legend']}>
            {formatMessage(messages.items, {
              count: lineItems ? getTotalItems(lineItems) : 0,
            })}
          </div>
          <div className={styles['summary-order-items-info']}>
            {subtotal !== null &&
              priceToString({
                price: subtotal ?? 0,
                formatNumber,
                formatMessage,
              })}
          </div>
        </div>
        {warrantyTotal !== null && (
          <div className={styles['summary-order-warranty-items']}>
            <div className={styles['summary-order-warranty-items-info']}>
              {formatMessage(messages.warranties)}
            </div>
            <div
              className={styles['summary-order-items-info']}
              data-testid="price-warranties"
            >
              {priceToString({
                price: warrantyTotal,
                formatNumber,
                formatMessage,
              })}
            </div>
          </div>
        )}
        {totalDiscount?.centAmount > 0 && (
          <div className={styles['summary-order-product-discounts']}>
            <div className={styles['summary-order-product-discounts-legend']}>
              {formatMessage(messages.productDiscounts)}
            </div>
            <div className={styles['summary-order-product-discounts-info']}>
              {totalDiscount !== null &&
                `-${priceToString({
                  price: totalDiscount,
                  formatNumber,
                  formatMessage,
                  forceDigit: true,
                })}`}
            </div>
          </div>
        )}
        {totalPromoBeddingDiscount?.centAmount ? (
          <div className={styles['summary-order-product-discounts']}>
            <div className={styles['summary-order-product-discounts-legend']}>
              {formatMessage(messages.beddingPromoTitle, {
                remaining: totalPromoBeddingDiscountRemaining
                  ? priceToString({
                      price: totalPromoBeddingDiscountRemaining,
                      formatNumber,
                      formatMessage,
                      forceDigit: true,
                    })
                  : 0,
              })}
            </div>
            <div className={styles['summary-order-product-discounts-info']}>
              {totalDiscount !== null &&
                `-${priceToString({
                  price: totalPromoBeddingDiscount,
                  formatNumber,
                  formatMessage,
                  forceDigit: true,
                })}`}
            </div>
          </div>
        ) : null}
        {totalPromo?.centAmount > 0 && (
          <div className={styles['summary-order-promos']}>
            <div className={styles['summary-order-promos-legend']}>
              {formatMessage(messages.promos)}
            </div>
            <div className={styles['summary-order-promos-info']}>
              {totalPromo !== null &&
                `-${priceToString({
                  price: totalPromo,
                  formatNumber,
                  formatMessage,
                  forceDigit: true,
                })}`}
            </div>
          </div>
        )}
        {totalGiftCards?.centAmount > 0 && (
          <div className={styles['summary-order-gift-cards']}>
            <div className={styles['summary-order-gift-cards-legend']}>
              {formatMessage(messages.giftCards, {
                count: giftCards?.length,
              })}
            </div>
            <div className={styles['summary-order-gift-cards-info']}>
              {totalPromo !== null &&
                `-${priceToString({
                  price: totalGiftCards,
                  formatNumber,
                  formatMessage,
                  forceDigit: true,
                })}`}
            </div>
          </div>
        )}
        <div className={styles['summary-order-shipping-cost']}>
          <div className={styles['summary-order-shipping-cost-legend']}>
            {formatMessage(messages.shippingCost)}
          </div>
          <div className={styles['summary-order-shipping-cost-info']}>
            {totalShipping !== null &&
              priceToString({
                price: totalShipping,
                formatNumber,
                formatMessage,
              })}
          </div>
        </div>
        {isQualifiedForFreeWhiteGloveShipping(customLineItems) && (
          <div
            className={
              styles['summary-order-shipping-cost-qualified-white-glove']
            }
          >
            {formatMessage(messages.qualifiedFreeWhiteGloveShipping)}
          </div>
        )}
        {mattressRemoval && (
          <div className={styles['summary-order-mattress-removal']}>
            <>
              <div className={styles['summary-order-mattress-removal-legend']}>
                {formatMessage(messages.mattressRemoval)}
              </div>
              <div className={styles['summary-order-mattress-removal-info']}>
                {formatMessage(utilMessages.free)}
              </div>
            </>
          </div>
        )}
        <div className={styles['summary-order-taxes']}>
          <div className={styles['summary-order-taxes-legend']}>
            {formatMessage(messages.taxes)}
          </div>
          <div className={styles['summary-order-taxes-info']}>
            {taxes !== null &&
              total !== null &&
              priceToString({
                price: {
                  centAmount: taxes,
                  currencyCode: total.currencyCode,
                  fractionDigits: total.fractionDigits,
                },
                formatNumber,
                formatMessage,
              })}
          </div>
        </div>
        <div className={styles['summary-order-recycling-fee']}>
          {recyclingFee && (
            <>
              <div className={styles['summary-order-recycling-fee-legend']}>
                {formatMessage(messages.recyclingFee)}
                <WhatIsThisFee />
              </div>

              <div className={styles['summary-order-recycling-fee-info']}>
                {taxes !== null &&
                  total !== null &&
                  priceToString({
                    price: {
                      centAmount: recyclingFee.centAmount,
                      currencyCode: recyclingFee.currencyCode,
                      fractionDigits: recyclingFee.fractionDigits,
                    },
                    formatNumber,
                    formatMessage,
                  })}
              </div>
            </>
          )}
        </div>
        <div className={styles['summary-order-delivery-fee']}>
          {deliveryFee && (
            <>
              <div className={styles['summary-order-delivery-fee-legend']}>
                {formatMessage(messages.deliveryFee)}
                <WhatIsThisFee />
              </div>

              <div className={styles['summary-order-delivery-fee-info']}>
                {taxes !== null &&
                  total !== null &&
                  priceToString({
                    price: {
                      centAmount: deliveryFee.centAmount,
                      currencyCode: deliveryFee.currencyCode,
                      fractionDigits: deliveryFee.fractionDigits,
                    },
                    formatNumber,
                    formatMessage,
                  })}
              </div>
            </>
          )}
        </div>
        <div className={styles['summary-order-total']}>
          <div className={styles['summary-order-total-legend']}>
            {formatMessage(messages.total)}
          </div>
          <div className={styles['summary-order-total-info']}>
            {total !== null &&
              priceToString({
                price: total ?? 0,
                formatNumber,
                formatMessage,
                forceDigit: true,
              })}
          </div>
        </div>
      </div>
      <div className={styles['billing-delivery']} data-private>
        <div className={styles['billing-delivery-title']}>
          {formatMessage(messages.billingAndDelivery)}
        </div>
        <div className={styles['billing-delivery-row']}>
          <div className={styles['billing-delivery-billing-block']}>
            <div className={styles['billing-delivery-billing-title']}>
              {formatMessage(messages.billing)}
            </div>
            <div className={styles['billing-delivery-billing-name']}>
              {`${order?.billingAddress?.firstName} ${order?.billingAddress?.lastName}`}
            </div>
            <div className={styles['billing-delivery-billing-street']}>
              {order?.billingAddress?.streetName?.toLowerCase()}
            </div>
            <div className={styles['billing-delivery-billing-street-number']}>
              {order?.billingAddress?.additionalStreetInfo &&
                `#${order?.billingAddress?.additionalStreetInfo}`}
            </div>
            <div className={styles['billing-delivery-billing-street-city']}>
              {order?.billingAddress &&
                `${order?.billingAddress?.city?.toLowerCase()}, ${
                  order?.billingAddress?.state
                } ${order?.billingAddress?.postalCode}`}
            </div>
          </div>

          <div className={styles['billing-delivery-payment-block']}>
            <div
              className={
                styles['billing-delivery-billing-payment-method-title']
              }
            >
              {formatMessage(messages.paymentMethod)}
            </div>

            {order?.paymentInfo?.payments.map(
              (payment) =>
                payment?.cardholderName && (
                  <div className={styles['billing-delivery-billing-name']}>
                    {payment.cardholderName}
                  </div>
                ),
            )}

            <div
              className={styles['billing-delivery-billing-payment-method-info']}
            >
              {displayPaymentMethods(order?.paymentInfo, formatMessage).map(
                (paymentMethodAsString) => (
                  <div key={paymentMethodAsString}>{paymentMethodAsString}</div>
                ),
              )}
            </div>
            {isCreditCard(order?.paymentInfo) && (
              <div>
                {displayCreditCardExpiry(order?.paymentInfo, formatMessage)}
              </div>
            )}
          </div>

          <div className={styles['billing-delivery-delivery-block']}>
            <div className={styles['billing-delivery-delivery-title']}>
              {formatMessage(messages.delivery)}
            </div>
            <div className={styles['billing-delivery-delivery-name']}>
              {`${order?.shippingAddress?.firstName} ${order?.shippingAddress?.lastName}`}
            </div>
            <div className={styles['billing-delivery-delivery-street']}>
              {order?.shippingAddress?.streetName?.toLowerCase()}
            </div>
            <div className={styles['billing-delivery-delivery-number']}>
              {order?.shippingAddress?.additionalStreetInfo &&
                `#${order?.shippingAddress?.additionalStreetInfo}`}
            </div>
            <div className={styles['billing-delivery-delivery-city']}>
              {order?.shippingAddress &&
                `${order?.shippingAddress?.city?.toLowerCase()}, ${
                  order?.shippingAddress?.state
                } ${order?.shippingAddress?.postalCode}`}
            </div>
          </div>

          <div className={styles['billing-delivery-method-block']}>
            <div className={styles['billing-delivery-delivery-method-title']}>
              {formatMessage(messages.deliveryMethod)}
            </div>
            <div className={styles['billing-delivery-delivery-method-info']}>
              {Object.keys(shipMethodItemMap)?.map((shippingMethod) => (
                <>
                  <div
                    className={
                      styles['billing-delivery-delivery-method-info-title']
                    }
                    key={shippingMethod}
                  >
                    {shippingMethod}
                  </div>
                  <div
                    className={styles['billing-delivery-delivery-method-items']}
                  >
                    {shipMethodItemMap[shippingMethod].map((item) => (
                      <div
                        className={
                          styles['billing-delivery-delivery-method-item']
                        }
                        key={item}
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                </>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankyouSummary;
